import { useEffect, useRef, useState } from "react";
import { Button, Group, List, Loader, Modal, Stack, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import HttpClient from "../helpers/HttpClient";
import { apiErrorMessage } from "../helpers/apiError";

interface ArticleUsage {
  kasse: number;
  invoices: number;
  stockMovements: number;
  rentals: number;
  inUse: boolean;
}

export type ArticleRemoval = "deleted" | "archived";

interface Props {
  // Article to remove; null = closed.
  article: { id: number; name: string } | null;
  // "/article" (Rechnungen → Artikel) or "/inventory/article" (Inventar)
  basePath: string;
  onClose: () => void;
  onDone: (result: ArticleRemoval) => void;
}

// Unused articles are deleted. Used ones (Kasse sales, invoices, stock
// bookings, rentals) can't be — deleting would break that history — so the
// dialog offers archiving instead: hidden from the Kasse, pickers and lists.
export function ArticleRemoveDialog({ article, basePath, onClose, onDone }: Props) {
  const [usage, setUsage] = useState<ArticleUsage | null>(null);
  const [working, setWorking] = useState(false);
  // Parents pass onClose inline; a ref keeps the usage lookup from re-running
  // on every render.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Keyed on the id: callers may pass a fresh { id, name } object each render.
  const articleId = article?.id ?? null;
  useEffect(() => {
    setUsage(null);
    if (articleId === null) return;
    new HttpClient<ArticleUsage>(`${basePath}/${articleId}/usage`)
      .getSingle()
      .then(setUsage)
      .catch(() => {
        notifications.show({ color: "red", message: "Verwendung konnte nicht geprüft werden." });
        onCloseRef.current();
      });
  }, [articleId, basePath]);

  if (!article) return null;

  const run = async () => {
    setWorking(true);
    try {
      if (usage?.inUse) {
        await new HttpClient(`${basePath}/${article.id}`).postAction("archive");
        notifications.show({ color: "blue", message: `„${article.name}“ archiviert.` });
        onDone("archived");
      } else {
        const res = await new HttpClient(basePath).delete(article.id);
        if (res.status !== 200) throw new Error();
        notifications.show({ color: "green", message: `„${article.name}“ gelöscht.` });
        onDone("deleted");
      }
      onClose();
    } catch (e) {
      notifications.show({ color: "red", message: apiErrorMessage(e, "Aktion fehlgeschlagen.") });
    } finally {
      setWorking(false);
    }
  };

  const uses = usage
    ? [
        usage.kasse > 0 && `${usage.kasse}× an der Kasse verkauft`,
        usage.invoices > 0 && `${usage.invoices}× auf Rechnungen`,
        usage.stockMovements > 0 && `${usage.stockMovements} Bestandsbuchungen`,
        usage.rentals > 0 && `${usage.rentals} Ausleihen`,
      ].filter(Boolean)
    : [];

  return (
    <Modal opened onClose={onClose} title={usage?.inUse ? "Artikel archivieren" : "Artikel löschen"}>
      {!usage ? (
        <Loader size="sm" />
      ) : (
        <Stack>
          {usage.inUse ? (
            <>
              <Text size="sm">
                <b>{article.name}</b> wird bereits verwendet und kann nicht gelöscht werden:
              </Text>
              <List size="sm">
                {uses.map((u) => <List.Item key={String(u)}>{u}</List.Item>)}
              </List>
              <Text size="sm">
                Stattdessen archivieren? Der Artikel verschwindet von der Kasse, aus der Artikelauswahl und den Listen.
                Bestehende Aufträge, Rechnungen und Buchungen bleiben erhalten. Er kann jederzeit wiederhergestellt werden.
              </Text>
            </>
          ) : (
            <Text size="sm">
              <b>{article.name}</b> wurde noch nirgends verwendet und wird endgültig gelöscht.
            </Text>
          )}
          <Group justify="flex-end">
            <Button variant="subtle" onClick={onClose}>Abbrechen</Button>
            <Button color={usage.inUse ? "orange" : "red"} onClick={run} loading={working}>
              {usage.inUse ? "Archivieren" : "Löschen"}
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
